/**
 * Game view — DOM rendering for the puzzle. Subscribes to the store and
 * re-renders on state changes.
 *
 * Tap-pair model only (PRD §7). No drag-drop in v1.
 */

import type { GameState } from '@/types/game';
import type { Puzzle } from '@/types/puzzle';
import { dayName } from '@/game/melbourne';
import { isCorrectMatch } from '@/game/engine';
import type { Store } from '@/game/state';
import { renderResultCard } from '@/ui/result-card';
import { fireConfetti } from '@/ui/confetti';
import { startTimer, type TimerHandle } from '@/game/timer';
import { trackEvent } from '@/analytics/gtag';

export interface MountGameOpts {
  root: HTMLElement;
  puzzle: Puzzle;
  store: Store;
  numericIdFromPuzzle: (id: string) => string;
  onWin: (state: GameState) => { streak: number };
}

export function mountGame(opts: MountGameOpts): () => void {
  const { root, puzzle, store, onWin, numericIdFromPuzzle } = opts;

  // Build the static layout once.
  root.innerHTML = '';

  // Slim instruction line — the hero slogan does the framing.
  const header = document.createElement('header');
  header.className = 'game-header';
  header.innerHTML = `
    <p class="game-instructions">Tap a phrase, then tap its plain-English meaning.</p>
  `;
  root.appendChild(header);

  // Live region for SR announcements.
  const live = document.getElementById('live-region');

  // Jargon list.
  const jargonList = document.createElement('div');
  jargonList.className = 'jargon-list';
  jargonList.setAttribute('role', 'group');
  jargonList.setAttribute('aria-label', 'Corporate jargon phrases');
  for (let i = 0; i < puzzle.phrases.length; i++) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'jargon-card';
    card.id = `jargon-${i}`;
    card.dataset.idx = String(i);
    card.setAttribute('aria-pressed', 'false');
    card.innerHTML = `<span class="jargon-text">${escapeHtml(puzzle.phrases[i].jargon)}</span>`;
    card.addEventListener('click', () => {
      store.dispatch({ type: 'select-jargon', idx: i });
    });
    jargonList.appendChild(card);
  }
  root.appendChild(jargonList);

  // Plain English divider.
  const divider = document.createElement('p');
  divider.className = 'divider-label';
  divider.textContent = 'Plain English';
  root.appendChild(divider);

  // Decoded chips (rendered after init has populated decodedOptions).
  const decodedList = document.createElement('div');
  decodedList.className = 'decoded-list';
  decodedList.setAttribute('role', 'group');
  decodedList.setAttribute('aria-label', 'Plain English options');
  root.appendChild(decodedList);

  // Timer.
  const timerBar = document.createElement('div');
  timerBar.className = 'timer-bar';
  timerBar.setAttribute('aria-label', 'Game timer');
  timerBar.innerHTML = `<span class="timer-icon" aria-hidden="true">⏱</span><span class="timer-value" id="timer-value">00:00</span>`;
  root.appendChild(timerBar);

  // Win-state mount point.
  const winContainer = document.createElement('div');
  winContainer.id = 'win-container';
  root.appendChild(winContainer);

  const timerEl = timerBar.querySelector('#timer-value') as HTMLElement;
  let timer: TimerHandle | null = null;

  // Track puzzle viewed.
  trackEvent({ name: 'puzzle_viewed', params: { puzzle_id: puzzle.id } });

  function renderDecoded(state: GameState): void {
    if (decodedList.childElementCount === state.decodedOptions.length) return;
    decodedList.innerHTML = '';
    for (let i = 0; i < state.decodedOptions.length; i++) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'decoded-chip';
      chip.id = `decoded-${i}`;
      chip.dataset.idx = String(i);
      chip.setAttribute('aria-pressed', 'false');
      chip.textContent = state.decodedOptions[i];
      chip.addEventListener('click', () => {
        const s = store.getState();
        if (s.selectedJargon === null) return;
        // Don't accept another decoded click while a match is mid-resolve.
        if (s.status === 'match-pending') return;
        store.dispatch({ type: 'select-decoded', idx: i, nowMs: performance.now() });
        const after = store.getState();
        if (after.selectedDecoded === null || after.selectedJargon === null) return;
        // Resolve correctness from the new state.
        const correct = isCorrectMatch(after, after.selectedJargon, after.selectedDecoded);
        const jIdx = after.selectedJargon;
        const dIdx = after.selectedDecoded;
        const jCard = document.getElementById(`jargon-${jIdx}`);
        const dChip = document.getElementById(`decoded-${dIdx}`);

        // Apply visual feedback immediately (shake / highlighter / matched).
        // Visual state is DOM-only and lives independently of the store —
        // the data-state attributes remain visible for the duration below
        // even after the store has resolved.
        if (correct) {
          if (jCard) {
            jCard.dataset.state = 'correct';
            const highlighter = document.createElement('span');
            highlighter.className = 'highlighter';
            highlighter.setAttribute('aria-hidden', 'true');
            jCard.appendChild(highlighter);
          }
          if (dChip) dChip.dataset.state = 'matched';
          if (live) live.textContent = 'Match correct.';
        } else {
          if (jCard) {
            jCard.dataset.state = 'wrong';
            setTimeout(() => {
              if (jCard.dataset.state === 'wrong') delete jCard.dataset.state;
            }, 250);
          }
          if (dChip) {
            dChip.dataset.state = 'wrong';
            setTimeout(() => {
              if (dChip.dataset.state === 'wrong') delete dChip.dataset.state;
            }, 250);
          }
          if (live) live.textContent = 'Wrong. Try again.';
        }

        // Dispatch resolve-match IMMEDIATELY so the store status returns to
        // idle and the user can re-click the same jargon card during the
        // shake animation without it being misinterpreted as a deselect.
        store.dispatch({
          type: 'resolve-match',
          jargonIdx: jIdx,
          decodedIdx: dIdx,
          correct,
          nowMs: performance.now(),
        });
      });
      decodedList.appendChild(chip);
    }
  }

  function render(state: GameState): void {
    renderDecoded(state);

    // Update jargon card aria-pressed and matched state.
    for (let i = 0; i < puzzle.phrases.length; i++) {
      const card = document.getElementById(`jargon-${i}`) as HTMLElement | null;
      if (!card) continue;
      const isSelected = state.selectedJargon === i;
      card.setAttribute('aria-pressed', String(isSelected));
      const isMatched = state.matches.some((m) => m.jargonIdx === i);
      if (isMatched && card.dataset.state !== 'correct') {
        card.dataset.state = 'matched';
      }
    }

    // Update decoded chip pressed/matched state.
    for (let i = 0; i < state.decodedOptions.length; i++) {
      const chip = document.getElementById(`decoded-${i}`) as HTMLElement | null;
      if (!chip) continue;
      const isSelected = state.selectedDecoded === i;
      chip.setAttribute('aria-pressed', String(isSelected));
      const isMatched = state.matches.some((m) => m.decodedIdx === i);
      if (isMatched) chip.dataset.state = 'matched';
    }

    // Start timer on first selection.
    if (state.startTime !== null && timer === null) {
      timer = startTimer(timerEl);
      timer.setStart(state.startTime);
      trackEvent({ name: 'puzzle_started', params: { puzzle_id: puzzle.id } });
    }

    // Win flow.
    if (state.won && winContainer.childElementCount === 0) {
      timer?.stop();
      const winInfo = onWin(state);

      // Win banner.
      const banner = document.createElement('section');
      banner.className = 'win-banner';
      banner.setAttribute('aria-live', 'polite');
      const seconds = Math.round(((state.endTime ?? 0) - (state.startTime ?? 0)) / 1000);
      banner.innerHTML = `
        <h2 class="win-banner-title">Decoded.</h2>
        <p class="win-banner-time">${formatSecondsHuman(seconds)}</p>
        <p class="win-banner-streak">Streak ${winInfo.streak}</p>
      `;
      winContainer.appendChild(banner);

      // Confetti.
      fireConfetti();

      // Result card after 400ms (slide-up handled by CSS).
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = reduced ? 0 : 400;
      setTimeout(() => {
        const card = renderResultCard({
          state,
          numericId: numericIdFromPuzzle(puzzle.id),
          streak: winInfo.streak,
        });
        winContainer.appendChild(card);
      }, delay);

      if (live) live.textContent = `Puzzle complete in ${seconds} seconds.`;

      trackEvent({
        name: 'puzzle_completed',
        params: {
          puzzle_id: puzzle.id,
          time_seconds: seconds,
          first_try_accuracy: state.matches.filter((m) => m.firstTry).length / 4,
        },
      });
    }
  }

  const unsubscribe = store.subscribe(render);
  render(store.getState());

  // Track abandons on page hide.
  const abandonHandler = () => {
    const s = store.getState();
    if (!s.won && s.matches.length > 0) {
      trackEvent({
        name: 'puzzle_abandoned',
        params: { puzzle_id: puzzle.id, matches_at_exit: s.matches.length },
      });
    }
  };
  window.addEventListener('pagehide', abandonHandler);

  return () => {
    unsubscribe();
    timer?.stop();
    window.removeEventListener('pagehide', abandonHandler);
  };
}

function formatSecondsHuman(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}m ${String(ss).padStart(2, '0')}s`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
